import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { PatientMonitoringType } from "../../../api";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { Select } from "../../../components/ui-kit/fields/select";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { LazyRichTextEditor } from "../../../components/ui-kit/lazy-rich-text-editor";
import {
  monitoringTypes,
  supportedLanguages,
} from "../../../constants/enum-to-text";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PhysiciansModal } from "../common/physicians-modal";

export type StudyTextsFormFields = {
  monitoring_type: PatientMonitoringType | null;
  study_text_language: string | null;
  study_text: string;
  country: string | null;
  region: number | null;
  clinic_uuid: string | null;
  physician_uuid: string | null;
  clinic_name: string;
  physician_name: string;
};

export const StudyTextsForm = observer(function StudyTextsForm({
  fields,
  admin,
}: {
  fields: StudyTextsFormFields;
  admin: AdminConsole;
}) {
  const { t } = useLingui();
  return (
    <>
      <FormTable dataAdd>
        <FormRow
          label={t`Monitoring Type`}
          mandatory
          data={
            <Select
              attr="monitoring_type"
              model={fields}
              values={monitoringTypes}
              required
            />
          }
        />
        <FormRow
          label={t`Study Text Language`}
          mandatory
          data={
            <Select
              attr="study_text_language"
              model={fields}
              values={supportedLanguages}
              required
            />
          }
        />
        <FormRow
          label={t`Country`}
          data={
            <CountrySelect
              attr="country"
              model={fields}
              onChange={() => {
                fields.region = null;
              }}
            />
          }
        />
        <FormRow
          label={t`Region`}
          data={
            <RegionSelect
              attr="region"
              model={fields}
              country={fields.country ?? undefined}
            />
          }
        />
        <FormRow
          label={t`Clinic`}
          data={
            <ChooseField
              label={fields.clinic_name || t`Choose clinic`}
              modal={({ onClose }) => {
                const clinics = admin.createClinics();
                return (
                  <ClinicsModal
                    title={t`Choose clinic`}
                    preselected={fields.clinic_uuid ? [fields.clinic_uuid] : []}
                    mode="single"
                    model={clinics}
                    onClose={onClose}
                    onConfirm={({ selected }) => {
                      fields.clinic_uuid = selected[0] ?? null;
                      fields.clinic_name =
                        clinics.data.find((c) => c.uuid === selected[0])
                          ?.name ?? "";
                    }}
                  />
                );
              }}
            />
          }
        />
        <FormRow
          label={t`Physician`}
          data={
            <ChooseField
              label={fields.physician_name || t`Choose physician`}
              modal={({ onClose }) => {
                const physicians = admin.createPhysicians();
                return (
                  <PhysiciansModal
                    title={t`Choose physician`}
                    preselected={
                      fields.physician_uuid ? [fields.physician_uuid] : []
                    }
                    mode="single"
                    model={physicians}
                    onClose={onClose}
                    onConfirm={({ selected }) => {
                      const p = physicians.data.find(
                        (ph) => ph.uuid === selected[0],
                      );
                      fields.physician_uuid = selected[0] ?? null;
                      fields.physician_name = p
                        ? `${p.given_name} ${p.family_name}`
                        : "";
                    }}
                  />
                );
              }}
            />
          }
        />
      </FormTable>
      <div className="border-t mt-4 pt-4">
        <label className="block font-bold mb-2">
          <Trans>
            Study Text Content:{" "}
            <span className="text-danger-foreground">*</span>
          </Trans>
        </label>
        <LazyRichTextEditor
          placeholder={t`Write a study text...`}
          value={fields.study_text}
          onChange={(v) => {
            fields.study_text = v;
          }}
          className="min-h-40"
        />
      </div>
    </>
  );
});
